/**
 * Receiving a delivery.
 *
 * Optimised for one situation: a van is outside, somebody is holding a waybill
 * in one hand and a phone in the other, and the driver wants to leave. Every
 * decision below serves that:
 *
 *  - **the scan field is the primary control**, focused and ready, because the
 *    fast path is beeping cartons off a pallet rather than searching a list;
 *  - **a second scan of the same carton adds to its line** rather than opening
 *    another, so the screen stays checkable against a one-line invoice;
 *  - **the running total is always visible**, because the whole point of
 *    checking a delivery is comparing it to what the invoice claims;
 *  - **nothing reaches the shelf until Post**, and the screen says so — a
 *    half-checked van must never look like stock.
 *
 * Posting is a separate, deliberate act with its own confirmation, because it
 * is the only thing here that changes what the business owns.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  PackagePlus,
  ScanLine,
  Trash2,
  Truck,
  X,
} from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { ApiError, api } from '../../lib/api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Modal,
  Spinner,
} from '../../components/ui';
import { useRecordLocation } from '../../lib/locationContext';
import { money, moment, quantity } from '../accountability/format';
import type { Receipt, ReceiptLine, ReceiptSummary, Supplier } from './types';

/** The list: past deliveries, and the way in to a new one. */
export function ReceivingPage(): ReactNode {
  const { business, can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currency = business?.currencyCode ?? 'USD';
  const timezone = business?.timezone ?? 'UTC';
  const path = (suffix: string) => `/businesses/${business?.businessId}${suffix}`;

  /**
   * Goods arrive somewhere, so a delivery note states where before it opens.
   *
   * The API refuses one that does not, rather than receiving a van-load into
   * whichever location happens to be the business default.
   */
  const location = useRecordLocation();
  const [supplierId, setSupplierId] = useState('');
  const [invoiceRef, setInvoiceRef] = useState('');
  const [error, setError] = useState<string | null>(null);

  const receipts = useQuery({
    queryKey: ['receipts', business?.businessId],
    queryFn: () => api.get<{ data: ReceiptSummary[] }>(path('/receipts')),
    enabled: Boolean(business),
  });

  const suppliers = useQuery({
    queryKey: ['suppliers', business?.businessId],
    queryFn: () => api.get<{ data: Supplier[] }>(path('/suppliers')),
    enabled: Boolean(business),
  });

  const start = useMutation({
    mutationFn: () =>
      api.post<Receipt>(path('/receipts'), {
        ...(supplierId ? { supplierId } : {}),
        // Where the goods are being received. Required.
        branchId: location.branchId,
        ...(invoiceRef.trim() ? { supplierInvoiceRef: invoiceRef.trim() } : {}),
        /**
         * A key the server can recognise if this request is retried.
         *
         * Without it a lost response means the next tap opens a second
         * delivery note for the same van, and the invoice then matches
         * neither of them.
         */
        idempotencyKey: `receipt-${business?.businessId}-${Date.now()}`,
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['receipts'] });
      navigate(`/receiving/${created.id}`);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not start a delivery.'),
  });

  const open = receipts.data?.data.find((r) => r.status === 'draft');

  return (
    <div className="space-y-4 pb-10">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Receiving</h1>
        <p className="text-sm text-slate-500">
          Check a delivery against its invoice before it reaches your stock.
        </p>
      </header>

      {error && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {error}
        </div>
      )}

      {can('receiving:manage') && (
        <Card>
          <CardHeader
            title={open ? 'A delivery is already open' : 'Start a delivery'}
            subtitle={
              open
                ? 'Finish or cancel it before starting another.'
                : 'Who brought it, and what does the waybill say?'
            }
          />
          <div className="flex flex-wrap items-end gap-2 p-4 pt-0">
            {!open && (
              <>
                {location.locations.length > 1 && (
                  <label className="text-xs text-slate-500">
                    Location
                    <select
                      value={location.branchId}
                      onChange={(e) => location.setBranchId(e.target.value)}
                      aria-label="Location"
                      className="mt-0.5 block rounded-lg border border-slate-200 px-2 py-2 text-sm"
                    >
                      <option value="">Choose a location</option>
                      {location.locations.map((place) => (
                        <option key={place.id} value={place.id}>
                          {place.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="text-xs text-slate-500">
                  Supplier
                  <select
                    value={supplierId}
                    onChange={(e) => setSupplierId(e.target.value)}
                    aria-label="Supplier"
                    className="mt-0.5 block rounded-lg border border-slate-200 px-2 py-2 text-sm"
                  >
                    <option value="">Not recorded</option>
                    {suppliers.data?.data.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs text-slate-500">
                  Invoice / waybill no.
                  <input
                    value={invoiceRef}
                    onChange={(e) => setInvoiceRef(e.target.value)}
                    aria-label="Invoice or waybill number"
                    placeholder="INV-4471"
                    className="mt-0.5 block rounded-lg border border-slate-200 px-2 py-2 text-sm"
                  />
                </label>
              </>
            )}

            {open ? (
              <Button onClick={() => navigate(`/receiving/${open.id}`)}>
                <Truck className="h-4 w-4" />
                Continue delivery
              </Button>
            ) : (
              <Button
                onClick={() => start.mutate()}
                disabled={start.isPending || location.unresolved}
                {...(location.unresolved
                  ? { title: 'Choose which location these goods are arriving at' }
                  : {})}
              >
                <PackagePlus className="h-4 w-4" />
                {start.isPending ? 'Starting…' : 'Start delivery'}
              </Button>
            )}
          </div>
        </Card>
      )}

      <Card>
        <CardHeader title="Past deliveries" subtitle="What arrived, and who signed for it." />
        <div className="p-4 pt-0">
          {receipts.isLoading && <Spinner label="Loading deliveries" />}
          {receipts.isError && <ErrorState message="Could not load deliveries." />}
          {receipts.data?.data.length === 0 && (
            <EmptyState
              title="No deliveries yet"
              description="Receiving through a delivery note is what lets you check stock against an invoice."
            />
          )}

          <ul className="space-y-2">
            {receipts.data?.data.map((receipt) => (
              <li key={receipt.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/receiving/${receipt.id}`)}
                  className="w-full rounded-xl border border-slate-200 p-3 text-left"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-medium text-slate-900">
                      {receipt.supplierName ?? 'Supplier not recorded'}
                    </span>
                    <Badge
                      tone={
                        receipt.status === 'posted'
                          ? 'success'
                          : receipt.status === 'draft'
                            ? 'info'
                            : 'neutral'
                      }
                    >
                      {receipt.status === 'draft' ? 'not yet received' : receipt.status}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {moment(receipt.receivedAt, timezone)}
                    {receipt.supplierInvoiceRef && <> · {receipt.supplierInvoiceRef}</>}
                    {receipt.postedBy && <> · posted by {receipt.postedBy}</>}
                  </p>
                  <p className="mt-1 text-xs text-slate-600">
                    {receipt.lines} item{receipt.lines === 1 ? '' : 's'} · {receipt.units} unit
                    {receipt.units === 1 ? '' : 's'}
                    {Number(receipt.value) > 0 && (
                      <span className="ml-2 tabular-nums">{money(receipt.value, currency)}</span>
                    )}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </Card>
    </div>
  );
}

/** The delivery screen itself: scan, check, post. */
export function ReceiptSessionPage(): ReactNode {
  const { id = '' } = useParams();
  const { business, can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currency = business?.currencyCode ?? 'USD';
  const timezone = business?.timezone ?? 'UTC';
  const path = (suffix: string) => `/businesses/${business?.businessId}${suffix}`;

  const [barcode, setBarcode] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastLineId, setLastLineId] = useState<string | null>(null);
  const field = useRef<HTMLInputElement>(null);

  const receipt = useQuery({
    queryKey: ['receipt', business?.businessId, id],
    queryFn: () => api.get<Receipt>(path(`/receipts/${id}`)),
    enabled: Boolean(business && id),
  });

  const addLine = useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<ReceiptLine>(path(`/receipts/${id}/lines`), input),
    onSuccess: (line) => {
      setLastLineId(line.id);
      setError(null);
      setBarcode('');
      void queryClient.invalidateQueries({ queryKey: ['receipt'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not add that item.'),
  });

  const removeLine = useMutation({
    mutationFn: (lineId: string) => api.del(path(`/receipts/${id}/lines/${lineId}`)),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['receipt'] }),
  });

  const post = useMutation({
    mutationFn: () => api.post<Receipt>(path(`/receipts/${id}/post`), {}),
    onSuccess: () => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ['receipt'] });
      void queryClient.invalidateQueries({ queryKey: ['receipts'] });
      void queryClient.invalidateQueries({ queryKey: ['catalogue'] });
    },
    onError: (caught) => {
      setConfirming(false);
      setError(caught instanceof ApiError ? caught.message : 'Could not post this delivery.');
    },
  });

  const cancel = useMutation({
    mutationFn: () => api.post(path(`/receipts/${id}/cancel`), {}),
    onSuccess: () => navigate('/receiving'),
  });

  // Ready for the next carton the instant the last one lands.
  useEffect(() => {
    if (!addLine.isPending) field.current?.focus();
  }, [addLine.isPending, lastLineId, error]);

  if (receipt.isLoading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner label="Loading the delivery" />
      </div>
    );
  }

  if (receipt.isError || !receipt.data) {
    return (
      <ErrorState
        message="Could not load this delivery."
        onRetry={() => void receipt.refetch()}
      />
    );
  }

  const data = receipt.data;
  const isDraft = data.status === 'draft';
  const editable = isDraft && can('receiving:manage');

  const scan = (): void => {
    const code = barcode.trim();
    if (code === '') return;
    addLine.mutate({
      barcode: code,
      quantity: 1,
      ...(unitCost.trim() ? { unitCost: unitCost.trim() } : {}),
    });
  };

  return (
    <div className="space-y-4 pb-24">
      <button
        type="button"
        onClick={() => navigate('/receiving')}
        className="inline-flex items-center gap-1 text-sm text-slate-600"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All deliveries
      </button>

      <header>
        <h1 className="text-xl font-semibold text-slate-900">
          {data.supplierName ?? 'Supplier not recorded'}
        </h1>
        <p className="text-sm text-slate-500">
          {data.supplierInvoiceRef && <>Invoice {data.supplierInvoiceRef} · </>}
          {moment(data.receivedAt, timezone)}
          {data.receivedBy && <> · opened by {data.receivedBy}</>}
          {data.postedBy && <> · posted by {data.postedBy}</>}
        </p>
      </header>

      {error && (
        <div
          role="alert"
          className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900"
        >
          {error}
        </div>
      )}

      {isDraft ? (
        /* The screen must never let a half-checked van look like stock. */
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Nothing here has been added to your stock yet. Post the delivery when it matches the
          invoice.
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          This delivery is {data.status} and can no longer be changed.
        </div>
      )}

      {editable && (
        <Card>
          <CardHeader
            title="Scan what came off the van"
            subtitle="Each scan adds one. Set a cost to value the delivery."
          />
          <div className="flex flex-wrap items-end gap-2 p-4 pt-0">
            <div className="min-w-0 flex-1">
              <label className="text-xs text-slate-500" htmlFor="receive-barcode">
                Barcode
              </label>
              <input
                id="receive-barcode"
                ref={field}
                type="text"
                value={barcode}
                autoComplete="off"
                disabled={addLine.isPending}
                placeholder="Scan or type"
                onChange={(e) => setBarcode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    scan();
                  }
                }}
                className="mt-0.5 w-full rounded-lg border border-slate-300 px-2 py-2 text-base tabular-nums disabled:bg-slate-100"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500" htmlFor="receive-cost">
                Unit cost
              </label>
              <input
                id="receive-cost"
                inputMode="decimal"
                type="text"
                value={unitCost}
                placeholder="—"
                onChange={(e) => setUnitCost(e.target.value.replace(/[^\d.]/g, ''))}
                className="mt-0.5 w-28 rounded-lg border border-slate-300 px-2 py-2 text-right text-base tabular-nums"
              />
            </div>
            <Button onClick={scan} disabled={addLine.isPending || barcode.trim() === ''}>
              <ScanLine className="h-4 w-4" />
              {addLine.isPending ? 'Adding…' : 'Add one'}
            </Button>
          </div>
        </Card>
      )}

      {/* What the invoice should say, pinned above the list. */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <Stat label="Items" value={String(data.totals.lines)} />
        <Stat label="Units" value={quantity(data.totals.units)} />
        <Stat label="Delivery value" value={money(data.totals.value, currency)} />
      </div>

      {data.totals.linesWithoutCost > 0 && (
        <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {data.totals.linesWithoutCost} item
          {data.totals.linesWithoutCost === 1 ? ' has' : 's have'} no cost. {' '}
          {data.totals.linesWithoutCost === 1 ? 'It' : 'They'} will still be received, but will
          not count toward the delivery value or your average cost.
        </p>
      )}

      {data.lines.length === 0 ? (
        <EmptyState
          title="Nothing on this delivery yet"
          description="Scan a carton, or add an item by hand from the catalogue."
        />
      ) : (
        <ul className="space-y-2">
          {data.lines.map((line) => (
            <li
              key={line.id}
              className={`rounded-xl border p-3 ${
                lastLineId === line.id ? 'border-indigo-300 ring-2 ring-indigo-500' : 'border-slate-200'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900">{line.productName}</p>
                  <p className="text-xs text-slate-500">
                    {line.productBarcode && (
                      <span className="tabular-nums">{line.productBarcode} · </span>
                    )}
                    {line.onHand !== null && <>{quantity(line.onHand)} in stock now</>}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-lg font-semibold tabular-nums">{quantity(line.quantity)}</p>
                  {line.unitCost && (
                    <p className="text-xs text-slate-500">
                      {money(line.unitCost, currency)} each
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-2 flex items-center justify-between">
                <p className="text-sm tabular-nums text-slate-700">
                  {line.lineTotal ? money(line.lineTotal, currency) : 'No cost recorded'}
                </p>
                {editable && (
                  <button
                    type="button"
                    onClick={() => removeLine.mutate(line.id)}
                    aria-label={`Remove ${line.productName}`}
                    className="inline-flex items-center gap-1 text-xs text-rose-700"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    Remove
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editable && (
        <div className="fixed inset-x-0 bottom-0 border-t border-slate-200 bg-white/95 p-3 backdrop-blur sm:static sm:border-0 sm:bg-transparent sm:p-0">
          <div className="mx-auto flex max-w-3xl items-center gap-2">
            <Button
              onClick={() => setConfirming(true)}
              disabled={data.totals.lines === 0}
              className="flex-1"
            >
              <Check className="h-4 w-4" />
              Post to stock
            </Button>
            <Button variant="ghost" onClick={() => cancel.mutate()}>
              <X className="h-4 w-4" />
              Cancel
            </Button>
          </div>
        </div>
      )}

      <Modal open={confirming} onClose={() => setConfirming(false)} title="Post this delivery?">
        <div className="space-y-3 text-sm text-slate-700">
          <p>
            This adds {quantity(data.totals.units)} unit
            {data.totals.units === 1 ? '' : 's'} across {data.totals.lines} item
            {data.totals.lines === 1 ? '' : 's'} to your stock, and records you as the person who
            received it.
          </p>

          {Number(data.totals.value) > 0 && (
            <p className="rounded-lg border border-slate-200 bg-slate-50 p-2">
              Delivery value {money(data.totals.value, currency)}. Check this against the invoice
              before posting.
            </p>
          )}

          <p className="text-xs text-slate-500">
            A posted delivery cannot be edited. If something is wrong afterwards, receive a
            correcting delivery — the record of what arrived is kept either way.
          </p>

          <div className="flex gap-2 pt-1">
            <Button onClick={() => post.mutate()} disabled={post.isPending}>
              {post.isPending ? 'Posting…' : 'Yes, post it'}
            </Button>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Not yet
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-2">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-base font-semibold tabular-nums text-slate-900">{value}</p>
    </div>
  );
}
